import { makeAutoObservable } from 'mobx';
import { fromPromise, type IPromiseBasedObservable } from 'mobx-utils';
import type PulseApi from '../../../api/PulseApi';
import Query, { type QueryParams } from '../../../store/utils/query/Query';
import type { QueryResponseDto } from '../../../store/utils/query/QueryResponse';
import { downloadUrl } from '../../../utils/browser';
import { getFirstMonthDay, getLastMonthDay } from '../../../utils/date';
import { onSuccess } from '../../../utils/errors';
import type AccountStore from '../../account/store/AccountStore';
import type AuthStore from '../../login/store/AuthStore';
import type { ProductStats } from '../../products/store/Product';
import type ProductStore from '../../products/store/ProductStore';
import PulseKeyStatRequest, {
  type PulseKeyStatDto,
  type PulseKeyStatParams,
} from '../../statistics/store/PulseKeyStatRequest';
import PulseKey, { type KeyStatus, type PulseKeyDto } from './PulseKey';
import PulseKeys from './PulseKeys';
import PulseKeySearch, { type KeySearchFilters } from './PulseKeySearch';

export default class PulseKeysStore {
  constructor(
    private readonly api: PulseApi,
    private readonly authStore: AuthStore,
    private readonly accountStore: AccountStore,
    private readonly productStore: ProductStore,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  async changeKeyCustomer(pulseKey: PulseKey, newCustomerEmail: string) {
    await this.api.pulseKeys.changeKeyCustomer(pulseKey.key, newCustomerEmail);
  }

  createKeySearch({
    autoSearch = true,
    serializable = true,
    params = {},
  }: {
    autoSearch?: boolean;
    params?: QueryParams<PulseKeyDto>;
    serializable?: boolean;
  } = {}): IPromiseBasedObservable<PulseKeySearch> {
    if (!this.accountStore.account) {
      throw new Error('Account is not loaded');
    }

    const { labels } = this.accountStore.account.configurations;
    return fromPromise(
      this.productStore.fetchAllProducts().then((products) => {
        return new PulseKeySearch(
          this.createQuery({ autoRun: false, ...params }),
          products.clone().sortByName(),
          labels,
          autoSearch,
          serializable,
        );
      }),
    );
  }

  createStatRequest(): IPromiseBasedObservable<PulseKeyStatRequest> {
    if (!this.accountStore.account) {
      throw new Error('Account is not loaded');
    }

    const { labels } = this.accountStore.account.configurations;

    const redeemedAtFrom = getFirstMonthDay();
    const redeemedAtTo = getLastMonthDay();

    const preloadPromises = Promise.all([
      this.productStore.fetchAllProducts(),
      this.fetchStats({
        productIds: [''],
        redeemedAtFrom,
        redeemedAtTo,
      }),
    ]);

    return fromPromise(
      preloadPromises.then(([products, initialStats]) => {
        return new PulseKeyStatRequest({
          getStats: this.fetchStats,
          initialStats,
          labels,
          products: products.clone().sortByName(),
          redeemedAtFrom,
          redeemedAtTo,
        });
      }),
    );
  }

  createQuery(
    params: QueryParams<PulseKeyDto> = {},
  ): Query<PulseKeyDto, PulseKeys> {
    return new Query({
      queryFunction: this.findKeys,
      transformFunction: (response) => new PulseKeys(response.items, response),
      params,
    });
  }

  createUnusedProductKeyQuery(
    sellableId: string,
  ): Query<PulseKeyDto, PulseKeys> {
    return this.createQuery({ sellableId, status: 'unused' });
  }

  async disableKey(pulseKey: PulseKey) {
    await this.api.pulseKeys.disableKey(pulseKey.key);
    pulseKey.setDisabled(true);
  }

  async enableKey(pulseKey: PulseKey) {
    await this.api.pulseKeys.enableKey(pulseKey.key);
    pulseKey.setDisabled(false);
  }

  async exportToCsv(pulseKeySearch: PulseKeySearch) {
    return downloadUrl(this.getExportToCsvUrl(pulseKeySearch), {
      fileName: 'pulse-keys.csv',
      token: this.authStore.session?.idToken,
    });
  }

  fetchProductKeys(sellableId: string): IPromiseBasedObservable<PulseKeys> {
    return fromPromise(
      this.api.pulseKeys.getProductKeys(sellableId).then((keysDto) => {
        return new PulseKeys(keysDto);
      }),
    );
  }

  fetchProductStats(productId: string): IPromiseBasedObservable<ProductStats> {
    return fromPromise(this.api.pulseKeys.getProductStats(productId));
  }

  fetchStats(
    params: PulseKeyStatParams,
  ): IPromiseBasedObservable<PulseKeyStatDto[]> {
    return fromPromise(this.api.pulseKeys.getStats(params));
  }

  findKeys(query: QueryParams): Promise<QueryResponseDto<PulseKeyDto>> {
    const trimmedQuery = Object.fromEntries(
      Object.entries(query).map(([key, value]) => {
        return typeof value === 'string' ? [key, value.trim()] : [key, value];
      }),
    );

    return this.api.pulseKeys.findKeys(trimmedQuery);
  }

  async generateKeys(
    sellableId: string,
    numOfKeys: number,
    label: string,
  ): Promise<PulseKeys> {
    const dtos = await this.api.pulseKeys.generateProductKeys(
      sellableId,
      numOfKeys,
      label,
    );

    return new PulseKeys(dtos);
  }

  getExportToCsvUrl(pulseKeysSearch: PulseKeySearch): string {
    const { filters } = pulseKeysSearch;

    if (!PulseKey.redeemedStatuses.includes(filters.status as KeyStatus)) {
      delete (filters as Partial<KeySearchFilters>).redeemedAtFrom;
      delete (filters as Partial<KeySearchFilters>).redeemedAtTo;
    }

    return this.api.pulseKeys.getExportToCsvUrl(filters);
  }

  async importSerials(
    sellableId: string,
    serials: string[],
    label: string,
    markDistributed = false,
  ) {
    await this.api.pulseKeys.importProductKeys(
      serials.map((key) => ({ key, label })),
      sellableId,
      markDistributed,
    );
  }

  async markKeysDistributed(pulseKeys: PulseKey[]) {
    const keyIds = pulseKeys.map((pulseKey) => pulseKey.key);
    await this.api.pulseKeys.markSpecifiedKeysDistributed(keyIds);
  }

  async markProductKeysDistributed(sellableId: string) {
    await this.api.pulseKeys.markAllProductKeysDistributed(sellableId);
  }

  async moveCustomerKeys(data: {
    newCustomerEmail: string;
    oldCustomerEmail: string;
  }) {
    await this.api.pulseKeys.moveCustomerKeys(data);
  }

  async moveBetweenProducts(data: { keyIds: string[]; newProductId: string }) {
    await this.api.pulseKeys.moveBetweenProducts(data);
  }

  async resetKeyDownloadCount(pulseKey: PulseKey) {
    await this.api.pulseKeys.resetDownloadCount(pulseKey.key);
    pulseKey.setDownloadCount(0);
  }

  async sendKeyToTheCustomer(pulseKey: PulseKey) {
    await this.api.pulseKeys.sendKeyToCustomer(pulseKey.key);

    onSuccess(
      `Key ${pulseKey.key} was successfully sent to ${pulseKey.customerEmail}`,
    );
  }

  async setKeysLabel(pulseKeys: PulseKey[], label: string) {
    await this.api.pulseKeys.setKeysLabel(
      pulseKeys.map(({ key }) => key),
      label,
    );
  }

  async unassignKey(pulseKey: PulseKey) {
    await this.api.pulseKeys.unassignKey(pulseKey.key);
  }
}
